import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  FaGuitar, 
  FaFire, 
  FaCheck, 
  FaStar,
  FaBolt,
  FaTags,
  FaMusic,
  FaSync,
  FaArrowRight,
  FaEye,
  FaChevronLeft,
  FaChevronRight,
  FaTools,
  FaCrown,
  FaGem,
  FaHandshake,
  FaRulerCombined,
  FaPaintBrush,
  FaPercent,
  FaPalette,
  FaExclamationTriangle
} from "react-icons/fa";
import { GiGuitarBassHead, GiGuitarHead } from "react-icons/gi";
import { collection, query, onSnapshot, orderBy, limit, where } from "firebase/firestore";
import { getDownloadURL, ref } from "firebase/storage";
import { db, storage } from "./firebase";
import './app.css';

// ==================== TYPES ====================
interface ParticleType {
  x: number;
  y: number;
  size: number;
  speedX: number;
  speedY: number;
  color: string;
  update: () => void;
  draw: () => void;
}

interface Guitar {
  id: string;
  name?: string;
  price?: number;
  originalPrice?: number;
  discount?: number;
  brand?: string;
  category?: string;
  shortDescription?: string;
  tags?: string[];
  stock?: number;
  featured?: boolean;
  bestSeller?: boolean;
  rating?: number;
  imageUrl?: string;
  imagePath?: string;
  imageUrls?: string[];
  createdAt?: any;
}

interface Category {
  title: string;
  description: string;
  icon: React.ComponentType<any>;
  color: string;
  slug: string;
  count: number;
}

interface GuitarCardProps {
  guitar: Guitar;
  index: number;
}

interface CategoryCardProps {
  category: Category;
}

// ==================== PROFESYONEL OPTİMİZE PARTİKEL BACKGROUND ====================
const ModernParticleBackground = React.memo(() => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number | null>(null);
  const particlesRef = useRef<ParticleType[]>([]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const particleCount = Math.min(35, Math.floor(window.innerWidth / 40));

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    
    resize();
    
    const resizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(resize);
    });
    
    resizeObserver.observe(canvas);

    class Particle implements ParticleType {
      x: number;
      y: number;
      size: number;
      speedX: number;
      speedY: number;
      color: string;

      constructor() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.size = Math.random() * 1.2 + 0.3;
        this.speedX = (Math.random() - 0.5) * 0.3;
        this.speedY = (Math.random() - 0.5) * 0.3;
        this.color = `hsla(${200 + Math.random() * 40}, 70%, 60%, ${Math.random() * 0.15 + 0.05})`;
      }

      update() {
        this.x += this.speedX;
        this.y += this.speedY;

        if (this.x > canvas.width) this.x = 0;
        else if (this.x < 0) this.x = canvas.width;
        if (this.y > canvas.height) this.y = 0;
        else if (this.y < 0) this.y = canvas.height;
      }

      draw() {
        ctx.fillStyle = this.color;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    particlesRef.current = Array.from({ length: particleCount }, () => new Particle());

    let lastTime = 0;
    const fps = 60;
    const interval = 1000 / fps;

    const animate = (timestamp: number) => {
      if (timestamp - lastTime < interval) {
        animationRef.current = requestAnimationFrame(animate);
        return;
      }
      
      lastTime = timestamp;

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      const gradient = ctx.createRadialGradient(
        canvas.width / 2,
        canvas.height / 2,
        0,
        canvas.width / 2,
        canvas.height / 2,
        Math.max(canvas.width, canvas.height) / 2
      );
      gradient.addColorStop(0, 'rgba(15, 23, 42, 0.1)');
      gradient.addColorStop(1, 'rgba(15, 23, 42, 0.3)');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      particlesRef.current.forEach(particle => {
        particle.update();
        particle.draw();
      });

      const particles = particlesRef.current;
      ctx.lineWidth = 0.2;
      
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const distance = dx * dx + dy * dy;

          if (distance < 90000) {
            ctx.beginPath();
            ctx.strokeStyle = `hsla(200, 70%, 60%, ${0.08 * (1 - Math.sqrt(distance) / 300)})`;
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.stroke();
          }
        }
      }

      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="fixed top-0 left-0 w-full h-full pointer-events-none z-0"
      style={{ imageRendering: 'crisp-edges' }}
    />
  );
});

ModernParticleBackground.displayName = 'ModernParticleBackground';

// ==================== HERO SLIDER COMPONENT ====================
const HeroSlider = React.memo(() => {
  const slides = useMemo(() => [
    {
      id: 1,
      title: "Premium Elektro Gitarlar",
      subtitle: "Profesyonel Kalitede",
      image: "https://images.unsplash.com/photo-1625159510807-03f1d1b52bff?fm=jpg&q=60&w=3000&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8M3x8Z3VpdGFyJTIwd2FsbHBhcGVyfGVufDB8fDB8fHww",
      cta: "Koleksiyonu Keşfet",
      color: "from-gray-900/20 to-orange-900/20",
      overlay: "from-gray-900/40 via-gray-900/20 to-transparent"
    },
    {
      id: 2,
      title: "Custom Shop",
      subtitle: "Kişiye Özel Tasarım",
      image: "https://images3.alphacoders.com/589/thumb-1920-58905.jpg",
      cta: "Özel Sipariş Ver",
      color: "from-gray-900/20 to-orange-900/20",
      overlay: "from-gray-900/40 via-gray-900/20 to-transparent"
    },
    {
      id: 3,
      title: "Limited Edition",
      subtitle: "Özel Seri Gitarlar",
      image: "https://blogger.googleusercontent.com/img/b/R29vZ2xl/AVvXsEhFludzF1uFBubD_smSr0d9bW7CxjEh9qzE1zuixrNQqEYgpEJ1TU5Ws7hgBld3fwoYqIFq-PXpNn1yNC83DwVNUBUqJLarcWBu7dUy_JzkHNiLh0GCDIO11bsdesZ91pociILxEebxI5os/w1200-h630-p-k-no-nu/Red+Fender+Stratocaster+Electric+Guitar+Bridge+Pickups+Whammy+Bar+Fretboard++Music+Desktop+HD+Wallpaper+1920x1080+Great+Guitar+Sound+www.GreatGuitarSound.Blogspot.com.jpg",
      cta: "Sınırlı Seri",
      color: "from-gray-900/20 to-orange-900/20",
      overlay: "from-gray-900/40 via-gray-900/20 to-transparent"
    }
  ], []);

  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [slides.length]);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  }, [slides.length]);

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  }, [slides.length]);

  return (
    <div className="relative w-full h-screen overflow-hidden">
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={currentSlide}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.8 }}
          className="absolute inset-0"
        >
          <div className={`absolute inset-0 bg-gradient-to-r ${slides[currentSlide].color}`} />
          <img
            src={slides[currentSlide].image}
            alt={slides[currentSlide].title}
            className="w-full h-full object-cover"
            loading="eager"
            decoding="async"
          />
          <div className={`absolute inset-0 bg-gradient-to-r ${slides[currentSlide].overlay}`} />
          
          <div className="absolute inset-0 flex items-center justify-center px-4 md:px-8 lg:px-12">
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="max-w-6xl text-center"
            >
              <motion.span 
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="inline-flex items-center gap-2 px-6 py-3 bg-white/10 backdrop-blur-md rounded-full text-lg font-semibold mb-6 border border-white/20"
              >
                <FaCrown className="text-gray-400" />
                {slides[currentSlide].subtitle}
              </motion.span>
              
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-bold mb-6 leading-tight tracking-tight">
                <span className="block">
                  {slides[currentSlide].title.split(' ')[0]}
                </span>
                <span className="block bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                  {slides[currentSlide].title.split(' ').slice(1).join(' ')}
                </span>
              </h1>
              
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.5 }}
              >
                <Link
                  to="/siparis"
                  className="inline-flex items-center gap-4 bg-white text-gray-900 px-8 py-4 rounded-full font-bold hover:bg-gray-100 transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:scale-105 text-lg active:scale-95"
                >
                  <FaGem className="text-blue-500" />
                  {slides[currentSlide].cta}
                  <FaArrowRight className="transition-transform duration-300 group-hover:translate-x-2" />
                </Link>
              </motion.div>
            </motion.div>
          </div>
        </motion.div>
      </AnimatePresence>

      <div className="absolute inset-x-4 top-1/2 -translate-y-1/2 flex justify-between pointer-events-none">
        <button
          onClick={prevSlide}
          className="pointer-events-auto w-12 h-12 sm:w-14 sm:h-14 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/20 transition-all border border-white/20 hover:scale-110 active:scale-95"
          aria-label="Önceki slide"
        >
          <FaChevronLeft className="text-white" size={20} />
        </button>
        <button
          onClick={nextSlide}
          className="pointer-events-auto w-12 h-12 sm:w-14 sm:h-14 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/20 transition-all border border-white/20 hover:scale-110 active:scale-95"
          aria-label="Sonraki slide"
        >
          <FaChevronRight className="text-white" size={20} />
        </button>
      </div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-2">
        {slides.map((_, idx) => (
          <button
            key={idx}
            onClick={() => setCurrentSlide(idx)}
            className={`w-2 h-2 rounded-full transition-all duration-300 hover:scale-125 ${
              currentSlide === idx 
                ? 'bg-white w-6' 
                : 'bg-white/30 hover:bg-white/50'
            }`}
            aria-label={`Slide ${idx + 1}`}
          />
        ))}
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/10">
        <motion.div
          key={currentSlide}
          initial={{ width: "0%" }}
          animate={{ width: "100%" }}
          transition={{ duration: 6, ease: "linear" }}
          className="h-full bg-gradient-to-r from-blue-500 to-gray-500"
        />
      </div>
    </div>
  );
});

HeroSlider.displayName = 'HeroSlider';

// ==================== UTILITY FUNCTIONS ====================
const calculateDiscount = (guitar: Guitar): number => {
  if (guitar.discount && typeof guitar.discount === 'number') {
    return Math.max(0, Math.min(100, Math.round(guitar.discount)));
  }
  
  if (guitar.originalPrice && guitar.price && guitar.originalPrice > guitar.price) {
    const discount = ((guitar.originalPrice - guitar.price) / guitar.originalPrice) * 100;
    return Math.max(0, Math.min(100, Math.round(discount)));
  }
  
  return 0;
};

const formatPrice = (price: number): string => {
  return new Intl.NumberFormat('tr-TR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(price);
};

// ==================== GUITAR CARD COMPONENT ====================
const GuitarCard: React.FC<GuitarCardProps> = React.memo(({ guitar, index }) => {
  const navigate = useNavigate();
  const [isHovered, setIsHovered] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageUrl, setImageUrl] = useState("");
  const [hasError, setHasError] = useState(false);
  const imageRef = useRef<HTMLImageElement>(null);

  const discount = useMemo(() => calculateDiscount(guitar), [guitar]);
  const tags = guitar.tags || [];

  useEffect(() => {
    const loadImage = async () => {
      if (!guitar) return;

      setImageLoaded(false);
      setHasError(false);

      try {
        if (guitar.imageUrls?.[0]) {
          setImageUrl(guitar.imageUrls[0]);
        } else if (guitar.imageUrl) {
          setImageUrl(guitar.imageUrl);
        } else if (guitar.imagePath) {
          const storageRef = ref(storage, guitar.imagePath);
          const url = await getDownloadURL(storageRef);
          setImageUrl(url);
        } else {
          setImageUrl("https://images.unsplash.com/photo-1516924962500-2b4b3b99ea02?q=80&w=1170&auto=format&fit=crop");
          setHasError(true);
        }
      } catch (error) {
        console.error('Image load error:', error);
        setImageUrl("https://images.unsplash.com/photo-1516924962500-2b4b3b99ea02?q=80&w=1170&auto=format&fit=crop");
        setHasError(true);
      }
    };

    loadImage();
  }, [guitar]);

  const handleCardClick = useCallback(() => {
    if (guitar?.id) {
      navigate(`/gitar/${guitar.id}`);
    }
  }, [guitar?.id, navigate]);

  if (!guitar) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      whileHover={{ y: -8, scale: 1.02 }}
      transition={{ duration: 0.3 }}
      className="group relative cursor-pointer"
      onClick={handleCardClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="relative bg-gradient-to-br from-gray-800/60 to-gray-900/60 backdrop-blur-sm rounded-2xl overflow-hidden border border-gray-700/50 hover:border-blue-500/40 transition-all duration-300 h-full flex flex-col shadow-lg hover:shadow-2xl">
       <div className="relative h-[400px] sm:h-[250px] md:h-[400px] lg:h-[400px] overflow-hidden flex-shrink-0">
          {hasError ? (
            <div className="absolute inset-0 bg-gradient-to-br from-gray-700 to-gray-800 flex items-center justify-center">
              <FaGuitar size={40} className="text-gray-600" />
            </div>
          ) : (
            <>
              {!imageLoaded && (
                <div className="absolute inset-0 bg-gradient-to-br from-gray-700 to-gray-800 animate-pulse" />
              )}
              <img
                ref={imageRef}
                src={imageUrl}
                alt={guitar.name || "Gitar"}
                className={`w-full h-full object-cover transition-transform duration-700 ${
                  isHovered ? 'scale-110' : 'scale-100'
                } ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
                loading="lazy"
                decoding="async"
                onLoad={() => setImageLoaded(true)}
                onError={() => {
                  setHasError(true);
                  setImageLoaded(true);
                }}
              />
            </>
          )}
          
          <div className="absolute inset-0 bg-gradient-to-b from-transparent to-gray-900/60" />
          
          {/* Badgeler */}
          <div className="absolute top-3 left-3 flex flex-col gap-1.5 z-20">
            {discount > 0 && (
              <motion.span 
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="bg-gradient-to-r from-red-500 to-pink-500 text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-lg flex items-center gap-1"
              >
                <FaPercent size={10} />
                %{discount} İNDİRİM
              </motion.span>
            )}
            {guitar.featured && (
              <motion.span 
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.1 }}
                className="bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1"
              >
                <FaStar size={10} />
                ÖNE ÇIKAN
              </motion.span>
            )}
            {guitar.bestSeller && (
              <motion.span 
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2 }}
                className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1"
              >
                <FaBolt size={10} />
                ÇOK SATAN
              </motion.span>
            )}
          </div>
          
          {(guitar.stock || 0) <= 0 && (
            <div className="absolute inset-0 bg-gradient-to-b from-red-900/60 to-red-900/80 flex items-center justify-center">
              <span className="bg-red-900/90 text-white px-4 py-2 rounded-full font-bold text-sm backdrop-blur-sm">
                STOKTA YOK
              </span>
            </div>
          )}
        </div>
        
        <div className="p-5 flex-grow flex flex-col">
          <div className="mb-4 flex-grow">
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1 min-w-0">
                <h3 className="font-bold text-white text-lg mb-1 line-clamp-1 group-hover:text-blue-400 transition-colors">
                  {guitar.name || "İsimsiz Gitar"}
                </h3>
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  {guitar.brand && (
                    <span className="px-2 py-1 bg-blue-500/20 text-blue-400 text-xs rounded-full">
                      {guitar.brand}
                    </span>
                  )}
                  {guitar.category && (
                    <span className="px-2 py-1 bg-gray-700/50 text-gray-300 text-xs rounded-full">
                      {guitar.category}
                    </span>
                  )}
                </div>
              </div>
            </div>
            
            {/* Kısa Açıklama */}
            {guitar.shortDescription && (
              <p className="text-gray-400 text-sm mb-3 line-clamp-2 leading-relaxed">
                {guitar.shortDescription}
              </p>
            )}
            
            {/* Etiketler */}
            {tags.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-4">
                {tags.slice(0, 3).map((tag, tagIndex) => (
                  <span
                    key={tagIndex}
                    className="px-2 py-1 bg-gradient-to-r from-purple-500/10 to-purple-600/10 text-purple-400 text-xs rounded-full border border-purple-500/20"
                  >
                    <FaTags size={10} className="inline mr-1" />
                    {tag}
                  </span>
                ))}
                {tags.length > 3 && (
                  <span className="px-2 py-1 bg-gray-800/50 text-gray-400 text-xs rounded-full">
                    +{tags.length - 3}
                  </span>
                )}
              </div>
            )}
          </div>
          
          <div className="mt-auto pt-4 border-t border-gray-700/50">
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xl font-bold text-white">
                    {formatPrice(guitar.price || 0)} ₺
                  </span>
                  {discount > 0 && (
                    <span className="text-xs text-red-400 font-semibold">
                      (%{discount})
                    </span>
                  )}
                </div>
                {guitar.originalPrice && guitar.originalPrice > (guitar.price || 0) && (
                  <div className="text-gray-500 text-sm line-through">
                    {formatPrice(guitar.originalPrice)} ₺
                  </div>
                )}
              </div>
              
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleCardClick();
                }}
                className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white px-4 py-2.5 rounded-xl font-medium hover:from-blue-600 hover:to-blue-700 transition-all duration-300 shadow-md hover:shadow-lg transform hover:scale-105 active:scale-95"
              >
                <FaEye size={14} />
                İncele
              </button>
            </div>
            
            <div className={`text-xs mt-2 flex items-center gap-2 ${(guitar.stock || 0) > 0 ? 'text-green-400' : 'text-red-400'}`}>
              {(guitar.stock || 0) > 0 ? (
                <>
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                  <span>Stok: {guitar.stock} adet</span>
                </>
              ) : (
                <>
                  <div className="w-2 h-2 bg-red-500 rounded-full" />
                  <span>STOKTA YOK</span>
                </>
              )}
            </div>
          </div>
        </div>
        
        {/* Hover efekti */}
        <div className={`absolute inset-0 bg-gradient-to-t from-blue-500/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none`} />
      </div>
      
      {/* Sıralama Badge (ilk 3 gitar için) */}
      {index < 3 && (
        <div className="absolute -top-3 -right-3 z-10">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center shadow-lg ${
            index === 0 ? 'bg-gradient-to-br from-yellow-500 to-gray-500' :
            index === 1 ? 'bg-gradient-to-br from-gray-400 to-gray-500' :
            'bg-gradient-to-br from-gray-700 to-orange-700'
          }`}>
            <span className="text-white font-bold text-sm">#{index + 1}</span>
          </div>
        </div>
      )}
    </motion.div>
  );
});

GuitarCard.displayName = 'GuitarCard';

// ==================== CATEGORY CARD COMPONENT ====================
const CategoryCard: React.FC<CategoryCardProps> = React.memo(({ category }) => {
  const Icon = category.icon;
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      whileHover={{ y: -5 }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className="group relative"
    >
      <div className={`absolute inset-0 bg-gradient-to-br ${category.color} rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-xl`} />
      
      <div className="relative bg-gray-800/30 backdrop-blur-sm rounded-2xl p-6 border border-gray-700/50 group-hover:border-transparent transition-all duration-300 overflow-hidden">
        <div className="absolute -right-4 -top-4 w-20 h-20 bg-gradient-to-br from-white/5 to-transparent rounded-full blur-sm" />
        
        <div className="flex items-center justify-between mb-6">
          <motion.div
            animate={isHovered ? { rotate: 10, scale: 1.1 } : { rotate: 0, scale: 1 }}
            transition={{ type: "spring", stiffness: 300 }}
            className="p-4 bg-gradient-to-br from-gray-700/50 to-gray-800/50 rounded-xl group-hover:bg-transparent transition-all duration-300"
          >
            <Icon className="text-3xl text-gray-300 group-hover:text-white" />
          </motion.div>
          <span className="px-3 py-1.5 bg-gray-700/50 rounded-full text-xs font-medium backdrop-blur-sm">
            {category.count} ürün
          </span>
        </div>
        
        <h3 className="text-xl font-bold text-white mb-3">{category.title}</h3>
        <p className="text-gray-400 text-sm mb-6 leading-relaxed line-clamp-2">
          {category.description}
        </p>
        
        <Link
          to={`/gitarlar?category=${category.slug}`}
          className="inline-flex items-center gap-2 text-blue-400 hover:text-blue-300 text-sm font-medium"
        >
          Koleksiyonu Gör
          <FaArrowRight className="transition-transform duration-300 group-hover:translate-x-1" size={12} />
        </Link>
      </div>
    </motion.div>
  );
});

CategoryCard.displayName = 'CategoryCard';

// ==================== LOADING SKELETON ====================
const LoadingSkeleton = React.memo(() => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
    {Array.from({ length: 4 }).map((_, i) => (
      <div key={i} className="bg-gray-800/30 rounded-2xl overflow-hidden animate-pulse">
        <div className="h-48 bg-gradient-to-r from-gray-700/50 to-gray-800/50" />
        <div className="p-5">
          <div className="h-5 bg-gradient-to-r from-gray-700/50 to-gray-700/30 rounded w-3/4 mb-3" />
          <div className="h-3 bg-gradient-to-r from-gray-700/50 to-gray-700/30 rounded w-1/2 mb-4" />
          <div className="flex justify-between">
            <div className="h-8 bg-gradient-to-r from-gray-700/50 to-gray-700/30 rounded w-20" />
            <div className="h-10 bg-gradient-to-r from-gray-700/50 to-gray-700/30 rounded w-24" />
          </div>
        </div>
      </div>
    ))}
  </div>
));

LoadingSkeleton.displayName = 'LoadingSkeleton';

// ==================== MAIN HOMEPAGE COMPONENT ====================
const AnaSayfa = () => {
  const [featuredGuitars, setFeaturedGuitars] = useState<Guitar[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    // Sayfa yüklendiğinde scroll'u sıfırla
    window.scrollTo(0, 0);
    
    return () => {
      setMounted(false);
    };
  }, []);

  const categories = useMemo<Category[]>(() => [
    {
      title: "Solid Body",
      description: "Rock & Metal için ideal sert ahşap gövde tasarımları",
      icon: FaGuitar,
      color: "from-red-500/20 to-orange-500/20",
      slug: "solid-body",
      count: 25
    },
    {
      title: "Hollow Body",
      description: "Jazz & Blues için özel tasarlanmış boş gövdeli gitarlar",
      icon: GiGuitarHead,
      color: "from-blue-500/20 to-cyan-500/20",
      slug: "hollow-body",
      count: 12
    },
    {
      title: "Bas Gitarlar",
      description: "Derin ve güçlü tonlar için profesyonel bas gitarlar",
      icon: GiGuitarBassHead,
      color: "from-purple-500/20 to-pink-500/20",
      slug: "bas-gitarlar",
      count: 18
    },
    {
      title: "Akustik Elektro",
      description: "Hem akustik hem elektrik performansı sunan çok yönlü modeller",
      icon: FaMusic,
      color: "from-green-500/20 to-emerald-500/20",
      slug: "akustik-elektro",
      count: 22
    },
  ], []);

  useEffect(() => {
    let isMounted = true;
    let unsubscribe: (() => void) | null = null;

    const fetchGuitars = async () => {
      try {
        if (!isMounted) return;
        
        setLoading(true);
        setError(null);

        const q = query(
          collection(db, "guitars"),
          where("featured", "==", true),
          orderBy("createdAt", "desc"),
          limit(8)
        );

        unsubscribe = onSnapshot(q, 
          async (snapshot) => {
            if (!isMounted) return;

            const guitarsPromises = snapshot.docs.map(async (doc) => {
              const data = doc.data() as Guitar;
              let imageUrl = data.imageUrl;
              
              if (data.imagePath) {
                try {
                  const storageRef = ref(storage, data.imagePath);
                  imageUrl = await getDownloadURL(storageRef);
                } catch (err) {
                  console.warn("Görsel yüklenemedi:", err);
                }
              }
              
              return {
                id: doc.id,
                ...data,
                imageUrl,
                discount: calculateDiscount(data)
              };
            });

            const guitars = await Promise.all(guitarsPromises);
            
            if (isMounted) {
              setFeaturedGuitars(guitars);
              setRetryCount(0);
            }
          },
          (err) => {
            if (!isMounted) return;
            console.error("Firebase error:", err);
            setError("Ürünler yüklenirken bir hata oluştu.");
            setRetryCount(prev => prev + 1);
          }
        );

      } catch (err) {
        if (!isMounted) return;
        console.error("Fetch error:", err);
        setError("Bağlantı hatası oluştu.");
        setRetryCount(prev => prev + 1);
      } finally {
        if (isMounted) {
          setTimeout(() => setLoading(false), 300);
        }
      }
    };

    fetchGuitars();

    return () => {
      isMounted = false;
      if (unsubscribe) unsubscribe();
    };
  }, [retryCount]);

  const handleRetry = () => {
    setRetryCount(prev => prev + 1);
  };

  // Sayfa yüklenene kadar basit bir loading
  if (!mounted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900" />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white overflow-x-hidden">
      <ModernParticleBackground />
      
      {/* Hero Section */}
      <section className="relative">
        <HeroSlider />
      </section>

      {/* Featured Products */}
      <section className="relative z-10 py-16 px-4 md:px-8 max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500/20 to-gray-500/20 rounded-full mb-4">
            <FaFire className="text-orange-400" />
            <span className="text-blue-400 font-semibold">ÖNE ÇIKAN ÜRÜNLER</span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
            Seçilmiş <span className="bg-gradient-to-r from-blue-400 to-gray-400 bg-clip-text text-transparent">Koleksiyon</span>
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Özenle seçilmiş premium elektro gitarlar
          </p>
        </motion.div>

        {error ? (
          <div className="text-center py-12">
            <div className="inline-flex p-4 bg-red-500/20 rounded-full mb-4">
              <FaExclamationTriangle className="text-2xl text-red-400" />
            </div>
            <p className="text-red-400 mb-4">{error}</p>
            <button
              onClick={handleRetry}
              className="inline-flex items-center gap-2 bg-gray-800 hover:bg-gray-700 px-6 py-3 rounded-full transition-colors"
            >
              <FaSync />
              Tekrar Dene
            </button>
          </div>
        ) : loading ? (
          <LoadingSkeleton />
        ) : featuredGuitars.length === 0 ? (
          <div className="text-center py-12">
            <div className="inline-flex p-4 bg-gray-700/50 rounded-full mb-4">
              <FaGuitar className="text-3xl text-gray-400" />
            </div>
            <p className="text-gray-400 mb-6">Henüz öne çıkan ürün bulunmuyor</p>
            <Link
              to="/gitarlar"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-500 to-gray-500 text-white px-6 py-3 rounded-full font-medium hover:from-blue-600 hover:to-gray-600 transition-all"
            >
              Tüm Ürünleri Gör
            </Link>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {featuredGuitars.map((guitar, index) => (
                <GuitarCard key={guitar.id} guitar={guitar} index={index} />
              ))}
            </div>
            
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="text-center mt-12"
            >
              <Link
                to="/gitarlar"
                className="inline-flex items-center gap-3 border-2 border-gray-700 hover:border-blue-500 text-gray-300 hover:text-white px-8 py-4 rounded-full font-bold transition-all duration-300 hover:scale-105 active:scale-95"
              >
                <FaGuitar className="transition-transform duration-300 group-hover:rotate-12" />
                Tüm Koleksiyonu Keşfet
                <FaArrowRight className="transition-transform duration-300 group-hover:translate-x-2" />
              </Link>
            </motion.div>
          </>
        )}
      </section>

      {/* Final CTA */}
      <section className="relative z-10 py-20 px-4 md:px-8 max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="relative overflow-hidden rounded-3xl"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900/20" />
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1583511666447-87e3c8c4c5ef?q=80&w=2070&auto=format&fit=crop')] bg-cover bg-center opacity-10" />
          
          <div className="relative z-10 text-center p-8 md:p-16">
            <motion.div
              initial={{ rotate: -180, scale: 0 }}
              animate={{ rotate: 0, scale: 1 }}
              transition={{ type: "spring" }}
              className="inline-flex p-4 bg-gradient-to-r from-gray-500/20 to-black rounded-full mb-8"
            >
              <FaTools className="text-4xl text-gray-400" />
            </motion.div>
            
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
              Rüyanızdaki <span className="bg-gradient-to-r from-gray-400 to-gray-200 bg-clip-text text-transparent">Custom Gitar</span> Burada
            </h2>
            <p className="text-gray-400 text-lg mb-10 max-w-3xl mx-auto leading-relaxed">
              Standart gitarlardan sıkıldınız mı? Kendi tarzınızı yansıtan, sadece size özel 
              bir gitar hayal edin. Neo Guitar olarak, hayalinizdeki custom gitarı 
              birlikte tasarlıyor, el işçiliği ile üretiyor ve size özel ayarlarla teslim ediyoruz.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/siparis"
                className="inline-flex items-center justify-center gap-3 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white px-8 py-4 rounded-full font-bold transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 min-w-[200px]"
              >
                <FaPalette className="transition-transform duration-300 group-hover:rotate-12" />
                Custom Gitar Sipariş Ver
                <FaArrowRight className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              
              <Link
                to="/siparis"
                className="inline-flex items-center justify-center gap-3 border-2 border-gray-700/30 text-gray-100 hover:text-gray-300 hover:border-gray-500 px-8 py-4 rounded-full font-bold transition-all duration-300 hover:scale-105 active:scale-95 min-w-[200px]"
              >
                <FaHandshake className="transition-transform duration-300 group-hover:scale-110" />
                Ücretsiz Tasarım Danışmanlığı
                <FaArrowRight className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
            
            <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6 max-w-3xl mx-auto">
              <div className="flex flex-col items-center gap-2 text-gray-300 p-4 rounded-xl bg-gray-900/30">
                <FaRulerCombined className="text-gray-400 text-2xl" />
                <span className="font-medium text-gray-100">Kişiye Özel Tasarım</span>
                <span className="text-xs text-gray-400">Vücut şeklinden manyetik seçimine</span>
              </div>
              <div className="flex flex-col items-center gap-2 text-gray-300 p-4 rounded-xl bg-gray-900/30">
                <FaPaintBrush className="text-gray-400 text-2xl" />
                <span className="font-medium text-gray-100">Özel Finis & Boya</span>
                <span className="text-xs text-gray-400">Sadece size özel renk ve desen</span>
              </div>
              <div className="flex flex-col items-center gap-2 text-gray-300 p-4 rounded-xl bg-gray-900/30">
                <FaStar className="text-gray-400 text-2xl" />
                <span className="font-medium text-gray-100">El İşçiliği</span>
                <span className="text-xs text-gray-400">Usta ellerde özenle üretim</span>
              </div>
            </div>
          </div>
        </motion.div>
      </section>
    </div>
  );
};

export default React.memo(AnaSayfa);